import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { inkOn } from './contrast';
import type { User } from '../api';
import type { PresenceUser } from '../editor/editor';
import { UserAvatars } from './StatusBar';
import { Wordmark } from './Logo';
import { AvatarContent, initials } from './Avatar';
import { setThemePref, useTheme, DARK_TONES, type ThemePref } from './theme';
import { getPrefs, setPref } from '../prefs';
import { showContextMenu, type MenuItem } from '../editor/contextmenu';
import { uiConfirm, uiAlert } from './Dialogs';
import { formatShortcut } from './shortcuts';
import { recordUsage, menuKey } from '../usage';
import { tookActionWithout } from '../shortcuttips';
import { canonical, effectiveShortcut, getBindings, isCustom, keyFromEvent, setBinding, subscribeBindings } from './keybindings';
import { useUiMode } from './uimode';
import { planBind, pendingLyxBind, clearPendingLyxBind, LYXBIND_EVENT, ctrlIPlan, ctrlINow } from './lyxbind';

/** the text tone of the dark theme (app/theme.ts DARK_TONES) */
export function darkToneMenuItems(): MenuItem[] {
  const current = getPrefs().darkTone;
  return [{ label: 'Text in the dark theme', info: true }, ...DARK_TONES.map(t => ({ label: t.label, checked: current === t.id, action: () => setPref('darkTone', t.id) }))];
}

/**
 * Smart invert (app/darkpages.ts): the pages — documents, PDFs, slides, posters, layout pages, one
 * switch for all — and with them their figures (line art; photographs keep their colours)
 */
export function smartInvertMenuItems(): MenuItem[] {
  const p = getPrefs();
  return [
    { label: 'Smart invert in the dark theme', info: true },
    { label: 'Pages — documents, PDFs, slides', checked: p.darkPages, action: () => setPref('darkPages', !p.darkPages) },
    { label: 'Figures too — photos keep their colours', checked: p.invertFigures, disabled: !p.darkPages, action: () => setPref('invertFigures', !p.invertFigures) },
  ];
}

/** the same choices for a menu bar's View menu (editorViewMenu.ts): one submenu, labels that stand alone in the command palette */
export function darkThemeMenuEntries(): MenuEntry[] {
  const p = getPrefs();
  return [{ label: 'Dark theme ▸', sub: [
    { label: 'Smart invert pages (documents, PDFs, slides)', checked: p.darkPages, action: () => setPref('darkPages', !p.darkPages) },
    { label: 'Smart invert figures too (photos keep their colours)', checked: p.invertFigures, disabled: !p.darkPages, action: () => setPref('invertFigures', !p.invertFigures) },
    { sep: true },
    ...DARK_TONES.map(t => ({ label: `${t.label} text`, checked: p.darkTone === t.id, action: () => setPref('darkTone', t.id) })),
  ] }];
}

/**
 * The theme switch's menu: Default (the system's theme — VS Code's in the extension), Light or
 * Dark; smart invert of the pages and their figures in the dark theme; the dark theme's text tone;
 * the Appearance settings where the shell has them.
 */
export function themeMenuItems(pref: ThemePref, pick: (p: ThemePref) => void, host = 'the system', onSettings?: () => void): MenuItem[] {
  return [
    { label: 'Theme', info: true },
    { label: `Default (follows ${host})`, checked: pref === 'system', action: () => pick('system') },
    { label: 'Light', checked: pref === 'light', action: () => pick('light') },
    { label: 'Dark', checked: pref === 'dark', action: () => pick('dark') },
    { sep: true },
    ...smartInvertMenuItems(),
    { sep: true },
    ...darkToneMenuItems(),
    ...(onSettings ? [{ sep: true }, { label: 'Appearance settings…', action: onSettings }] : []),
  ];
}

const THEME_ICONS: Record<ThemePref, string> = {
  light: '<circle cx="12" cy="12" r="4.2" /><path d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8" />',
  dark: '<path d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11z" />',
  // half light, half dark: whichever the system (or VS Code) shows
  system: '<circle cx="12" cy="12" r="8.5" /><path d="M12 3.5a8.5 8.5 0 0 1 0 17z" fill="currentColor" stroke="none" />',
};

/**
 * The theme switch (sun = light, moon = dark, half circle = default): a click or a right-click
 * opens themeMenuItems. Without props it uses the browser's preference (theme.ts, the web
 * client); the VS Code shell passes its own preference, what it shows and `host` = "VS Code".
 */
export function ThemeToggle({ pref: givenPref, dark: shownDark, onPick, host = 'the system', onSettings }: { pref?: ThemePref; dark?: boolean; onPick?: (p: ThemePref) => void; host?: string; onSettings?: () => void } = {}) {
  const { theme, pref: ownPref } = useTheme();
  const pref = givenPref ?? ownPref;
  const dark = shownDark ?? theme === 'dark';
  // a click while the menu is open closes it (the menu's own mousedown handler) and must not reopen it
  const wasOpen = useRef(false);
  const menu = (x: number, y: number) => {
    showContextMenu(x, y, themeMenuItems(pref, onPick ?? setThemePref, host, onSettings));
    document.querySelector('.ctx-menu')?.setAttribute('data-theme-menu', '');
  };
  const shown = pref === 'system' ? `default (${dark ? 'dark' : 'light'}, as ${host})` : dark ? 'dark' : 'light';
  return (
    <button type="button" class="theme-toggle" data-theme-toggle data-current={dark ? 'dark' : 'light'} data-pref={pref} aria-haspopup="menu"
      title={`Theme: ${shown} — default, light or dark; smart invert of pages and figures and the text tone in the dark theme`}
      onPointerDown={() => { wasOpen.current = !!document.querySelector('.ctx-menu[data-theme-menu]'); }}
      onClick={e => {
        if (wasOpen.current) { wasOpen.current = false; return; }
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        menu(r.left, r.bottom + 4);
      }}
      onContextMenu={e => { e.preventDefault(); menu(e.clientX, e.clientY); }}>
      <svg viewBox="0 0 24 24" aria-hidden="true" dangerouslySetInnerHTML={{ __html: THEME_ICONS[pref] }} />
    </button>
  );
}

export interface MenuEntry {
  label?: string;
  /** what the entry is known by when its label differs (the Modern interface's plain names): custom
   *  shortcuts, the usage statistics and the palette's ids keep the Classic label (see documentMenus `plain`) */
  id?: string;
  shortcut?: string; action?: () => void; checked?: boolean; disabled?: boolean; sep?: boolean; sub?: MenuEntry[];
  /** what the usage statistics record instead of the label — for entries whose label is document content (the Navigate menu's headings) */
  stat?: string;
}
/** `search`: the menu starts with the command palette (a search over all menus and shortcuts) — the Help menu */
export interface MenuDef { title: string; items: MenuEntry[]; search?: boolean }
/**
 * Something the palette can find: a menu item (with its menu path) or a reference entry (a
 * shortcut from the table). `id` = the menu path, the key of a user shortcut; `fixed` entries
 * cannot be given one (the shortcut is informational).
 */
export interface SearchEntry { id: string; label: string; path: string[]; shortcut?: string; checked?: boolean; action?: () => void; fixed?: boolean; stat?: string }

/** the Help menu item that opens the palette (its id = 'Help ▸ ' + label); rebindable like any other */
export const PALETTE_LABEL = 'Search menus and shortcuts (command palette)';
export const PALETTE_DEFAULT = 'Ctrl+Shift+P';
const PALETTE_ID = 'Help ▸ ' + PALETTE_LABEL;
/** opens the palette from outside (the Help menu item) */
export const openPalette = () => window.dispatchEvent(new Event('ol:palette'));

const cleanLabel = (s: string) => s.replace(/\s*▸\s*$/, '').replace(/…$/, '').trim();
const entryId = (path: string[], label: string) => [...path, cleanLabel(label)].join(' ▸ ');

/** every runnable item of every menu, with its path (File ▸ Export ▸ …) */
export function collectEntries(menus: MenuDef[]): SearchEntry[] {
  const out: SearchEntry[] = [];
  const walk = (items: MenuEntry[], path: string[]) => {
    for (const it of items) {
      if (it.sep || !it.label) continue;
      if (it.sub) { walk(it.sub, [...path, cleanLabel(it.id ?? it.label)]); continue; }
      if (it.disabled || !it.action) continue;
      out.push({ id: entryId(path, it.id ?? it.label), label: cleanLabel(it.label), path, shortcut: it.shortcut, checked: it.checked, action: it.action, stat: it.stat });
    }
  };
  for (const m of menus) walk(m.items, [m.title]);
  return out;
}

const norm = (s: string) => s.toLowerCase().replace(/[⌘⌥⇧⌃]/g, c => ({ '⌘': 'cmd ', '⌥': 'alt ', '⇧': 'shift ', '⌃': 'control ' })[c] ?? c);

/** ranked matches: label start, a word start in the label, inside the label, the path, the shortcut (every word of the query must occur) */
export function searchEntries(entries: SearchEntry[], query: string, limit = 12): SearchEntry[] {
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const scored: [number, SearchEntry][] = [];
  for (const e of entries) {
    const shortcut = effectiveShortcut(e.id, e.shortcut) ?? '';
    const label = norm(e.label), path = norm(e.path.join(' ')), sc = norm(shortcut + ' ' + formatShortcut(shortcut));
    let score = 0;
    for (const w of words) {
      const s = label.startsWith(w) ? 5 : (' ' + label).includes(' ' + w) ? 4 : label.includes(w) ? 3 : path.includes(w) ? 2 : sc.includes(w) ? 1 : 0;
      if (!s) { score = 0; break; }
      score += s;
    }
    if (score) scored.push([score, e]);
  }
  return scored.sort((a, b) => b[0] - a[0] || a[1].label.length - b[1].label.length).slice(0, limit).map(x => x[1]);
}

/** re-render when the user's shortcuts change */
function useBindings() {
  const [, tick] = useState(0);
  useEffect(() => subscribeBindings(() => tick(t => t + 1)), []);
  return getBindings();
}

function MenuList({ items, path, close, style, back, escapeCloses }: { items: MenuEntry[]; path: string[]; close: () => void; style?: string; back?: () => void; /** Escape closes the menu even with `back` (← still goes back): the Modern panel */ escapeCloses?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => { ref.current?.querySelector<HTMLElement>(':scope > [role="menuitem"]:not([aria-disabled="true"])')?.focus(); }, []);
  const onKey = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement).closest('[role="menu"]') !== ref.current) return;
    const entries = [...ref.current!.querySelectorAll<HTMLElement>(':scope > [role="menuitem"]:not([aria-disabled="true"])')];
    const at = entries.indexOf(document.activeElement as HTMLElement);
    let next: HTMLElement | undefined;
    if (e.key === 'ArrowDown') next = entries[(at + 1) % entries.length];
    else if (e.key === 'ArrowUp') next = entries[(at - 1 + entries.length) % entries.length];
    else if (e.key === 'Home') next = entries[0];
    else if (e.key === 'End') next = entries.at(-1);
    else if (e.key === 'Escape' || (e.key === 'ArrowLeft' && back)) { e.preventDefault(); e.stopPropagation(); (e.key === 'Escape' && escapeCloses ? close : back ?? close)(); return; }
    else if (e.key === 'Tab') { close(); return; }
    else if (e.key.length === 1 && /[a-z0-9]/i.test(e.key)) next = [...entries.slice(at + 1), ...entries.slice(0, at + 1)].find(el => el.textContent?.trim().toLowerCase().startsWith(e.key.toLowerCase()));
    if (next) { e.preventDefault(); e.stopPropagation(); next.focus(); }
  };
  return <div class="menu-list" ref={ref} role="menu" aria-label={path.at(-1)} style={style} onKeyDown={onKey} onMouseDown={e => e.preventDefault()}>
    {items.map((it, i) => {
      if (it.sep) return <div key={i} class="menu-sep" role="separator" />;
      if (it.sub) return <SubMenuItem key={i} entry={it} path={path} close={close} />;
      const sc = it.label ? effectiveShortcut(entryId(path, it.id ?? it.label), it.shortcut) : it.shortcut;
      // the shortcut tip goes where the entry was (the menu closes)
      const run = (row: EventTarget | null) => {
        if (it.disabled) return;
        const at = (row as Element | null)?.getBoundingClientRect();
        close(); recordUsage('menu', menuKey(path, it.stat ?? it.id ?? it.label ?? '')); tookActionWithout(sc, it.label ?? '', at); it.action?.();
      };
      return <div key={i} role="menuitem" tabIndex={-1} aria-disabled={!!it.disabled} class={'menu-item' + (it.checked ? ' checked' : '') + (it.disabled ? ' disabled' : '')} onClick={e => run(e.currentTarget)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); run(e.currentTarget); } }}><span>{it.label}</span>{sc && <span class="shortcut">{formatShortcut(sc)}</span>}</div>;
    })}
  </div>;
}

function SubMenuItem({ entry, path, close }: { entry: MenuEntry; path: string[]; close: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const open = () => {
    const anchor = ref.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const h = Math.min((entry.sub!.length + 1) * 24, window.innerHeight * .8);
    // no room beside the item (a phone): below it — or above —, never over it: the submenu opened under
    // the finger and the tap's click landed on its first entry (Text Style ▸ applied Italic)
    if (r.right + 240 > window.innerWidth - 8 && r.left - 240 < 8) {
      const below = r.bottom + h <= window.innerHeight - 8;
      setPosition({ left: Math.max(8, Math.min(r.left + 16, window.innerWidth - 248)), top: below ? r.bottom : Math.max(8, r.top - h) });
      return;
    }
    setPosition({ left: r.right + 240 > window.innerWidth - 8 ? Math.max(8, r.left - 240) : r.right, top: Math.max(8, Math.min(r.top, window.innerHeight - h)) });
  };
  return <div ref={ref} role="menuitem" tabIndex={-1} aria-haspopup="menu" aria-expanded={!!position} class="menu-item menu-sub" onMouseEnter={open} onMouseLeave={() => setPosition(null)} onClick={e => { if ((e.target as HTMLElement).closest('[role="menuitem"]') !== ref.current) return; e.stopPropagation(); open(); }} onKeyDown={e => {
    if (e.target !== ref.current) return;
    if (['ArrowRight', 'Enter', ' '].includes(e.key)) { e.preventDefault(); e.stopPropagation(); open(); }
  }}><span>{cleanLabel(entry.label ?? '')}</span>
    {position && <MenuList items={entry.sub!} path={[...path, cleanLabel(entry.id ?? entry.label ?? '')]} close={close} back={() => { setPosition(null); ref.current?.focus(); }} style={`position:fixed;top:${position.top}px;left:${position.left}px`} />}
  </div>;
}

/**
 * The command palette (Help menu): a search box over every menu item and shortcut, then the
 * menu's own items. Every result has a ✎ button that records a new shortcut for it — press the
 * keys; Backspace removes the shortcut, Esc cancels; a key that another command uses asks first.
 */
function SearchMenu({ menu, entries, close, recording, setRecording, paletteShortcut }: { menu: MenuDef; paletteShortcut: string; entries: SearchEntry[]; close: () => void; recording: string | null; setRecording: (id: string | null) => void }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const results = useMemo(() => searchEntries(entries, q), [entries, q]);
  useLayoutEffect(() => { input.current?.focus(); }, []);
  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => () => setRecording(null), []);
  const run = (e: SearchEntry, row?: Element | null) => {
    const at = row?.getBoundingClientRect();
    close(); recordUsage('palette', menuKey(e.path, e.stat ?? e.label)); tookActionWithout(effectiveShortcut(e.id, e.shortcut), e.stat ?? e.label, at); e.action?.();
  };

  /** a recorded key for `id`: collisions are confirmed, the other command then loses the key */
  const assign = async (id: string, key: string) => {
    const me = entries.find(e => e.id === id);
    const other = entries.find(e => e.id !== id && canonical(effectiveShortcut(e.id, e.shortcut)) === key);
    if (other) {
      const ok = await uiConfirm('Shortcut Already Used', `${formatShortcut(key)} is already used by "${[...other.path, other.label].join(' ▸ ')}".\n\nUse it for "${[...(me?.path ?? []), me?.label ?? id].join(' ▸ ')}" instead? The other command keeps working from the menu, without a shortcut.`, { okLabel: 'Reassign' });
      if (!ok) return;
      setBinding(other.id, null);
    }
    setBinding(id, key);
    setRecording(null);
  };
  const onKey = (ev: KeyboardEvent) => {
    if (recording) {
      ev.preventDefault(); ev.stopPropagation();
      if (ev.key === 'Escape') { setRecording(null); return; }
      if ((ev.key === 'Backspace' || ev.key === 'Delete') && !ev.ctrlKey && !ev.altKey && !ev.metaKey) { setBinding(recording, null); setRecording(null); return; }
      const k = keyFromEvent(ev);
      if (k) void assign(recording, k);
      return;
    }
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setSel(s => Math.min(results.length - 1, s + 1)); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setSel(s => Math.max(0, s - 1)); }
    else if (ev.key === 'Enter') { ev.preventDefault(); if (results[sel]) run(results[sel], input.current); }
    else if (ev.key === 'Escape') { ev.preventDefault(); close(); }
    ev.stopPropagation();
  };
  return (
    <div class="menu-list help-menu" onMouseDown={e => e.preventDefault()}>
      <div class="menu-search" onMouseDown={e => e.stopPropagation()}>
        <input ref={input} value={q} placeholder={`Search menus and shortcuts (${formatShortcut(effectiveShortcut(PALETTE_ID, paletteShortcut) ?? 'F1')})`} data-help-search
          onInput={e => setQ((e.target as HTMLInputElement).value)} onKeyDown={onKey} autocomplete="off" spellcheck={false} />
      </div>
      {q.trim() ? (
        results.length ? results.map((r, i) => {
          const sc = effectiveShortcut(r.id, r.shortcut);
          const custom = isCustom(r.id);
          return (
            <div key={r.id} class={'menu-result' + (i === sel ? ' sel' : '') + (r.checked ? ' checked' : '') + (recording === r.id ? ' recording' : '')} data-help-result onMouseEnter={() => setSel(i)} onClick={e => { if (recording !== r.id) run(r, e.currentTarget); }}>
              <span class="label"><span class="path">{r.path.join(' ▸ ')} ▸ </span>{r.label}</span>
              {recording === r.id
                ? <span class="rec" data-recording>Press the new keys… (Backspace: none, Esc: cancel)</span>
                : <>
                  {sc && <span class={'shortcut' + (custom ? ' custom' : '')} title={custom ? 'Your shortcut' : undefined}>{formatShortcut(sc)}</span>}
                  {custom && <button type="button" class="kb" data-reset-shortcut title="Back to the default shortcut" onClick={e => { e.stopPropagation(); setBinding(r.id, undefined); }}>↺</button>}
                  {!r.fixed && <button type="button" class="kb" data-set-shortcut title={sc ? 'Change the shortcut' : 'Set a shortcut'} onClick={e => { e.stopPropagation(); setRecording(r.id); input.current?.focus(); }}>{sc ? '✎' : '+ key'}</button>}
                </>}
            </div>
          );
        }) : <div class="menu-empty">No menu item or shortcut matches “{q.trim()}”.</div>
      ) : (
        menu.items.map((it, i) => {
          if (it.sep) return <div key={i} class="menu-sep" />;
          const sc = it.label ? effectiveShortcut(entryId([menu.title], it.id ?? it.label), it.shortcut) : it.shortcut;
          return (
            <div key={i} class={'menu-item' + (it.checked ? ' checked' : '') + (it.disabled ? ' disabled' : '')} onClick={e => { if (!it.disabled) { const at = e.currentTarget.getBoundingClientRect(); close(); tookActionWithout(sc, it.label ?? '', at); it.action?.(); } }}>
              <span>{it.label}</span>{sc && <span class="shortcut">{formatShortcut(sc)}</span>}
            </div>
          );
        })
      )}
      <div class="menu-hint">↑↓ Enter runs a command · ✎ sets its shortcut</div>
    </div>
  );
}

export function MenuBar({ menus, user, right, primary, title, onLogout, onSettings, onAppearance, onHome, searchEntries: extra = [], users, onJumpToUser, onShare, shareTitle, onSignIn, showThemeToggle = true, paletteShortcut = PALETTE_DEFAULT, captureF1 = true }: {
  menus: MenuDef[]; user?: User; right?: ComponentChildren; primary?: ComponentChildren; onLogout?: () => void; onSettings?: () => void; onHome?: () => void; showThemeToggle?: boolean; paletteShortcut?: string; captureF1?: boolean;
  /** the theme switch's "Appearance settings…" */
  onAppearance?: () => void;
  /** the document's name above the menus (the Modern interface's header, Google Docs' layout; Classic shows `right`) */
  title?: ComponentChildren;
  /** reference entries (shortcuts without a menu item) for the palette */
  searchEntries?: SearchEntry[];
  /** who is in the document right now (Google-Docs style, top right; click one to jump to their cursor) */
  users?: PresenceUser[];
  onJumpToUser?: (u: PresenceUser) => void;
  /** the Share button (top right) — only for the project's owner */
  onShare?: (() => void) | null;
  shareTitle?: string;
  /** a guest (came in through a share link): the Sign in button takes the Share button's place */
  onSignIn?: () => void;
}) {
  const [userOpen, setUserOpen] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [compact, setCompact] = useState(() => window.innerWidth < 1100);
  const barRef = useRef<HTMLDivElement>(null);
  // the Modern interface has no row of menus: one menu button beside the name opens them all
  // (menu names on the left, the one pointed at on the right); narrow, it drills down as in Classic
  const modern = useUiMode() === 'modern';
  const [recording, setRecording] = useState<string | null>(null);
  const bindings = useBindings();
  /** whatever had the keyboard before the palette took it (the editor, usually) gets it back */
  const prevFocus = useRef<HTMLElement | null>(null);
  const searchIndex = menus.findIndex(m => m.search);
  const entries = useMemo(() => [...collectEntries(menus), ...extra], [menus, extra]);
  // the user's shortcuts in force (key → command) and the default keys that no longer apply
  const keyIndex = useMemo(() => {
    const custom = new Map<string, SearchEntry>();
    const shadowed = new Set<string>();
    for (const e of entries) {
      if (e.fixed || !e.action) continue;
      if (!isCustom(e.id)) continue;
      const k = canonical(effectiveShortcut(e.id, e.shortcut));
      if (k) custom.set(k, e);
      const d = canonical(e.shortcut);
      if (d && d !== k) shadowed.add(d);
    }
    return { custom, shadowed };
  }, [entries, bindings]);
  // a LyX .bind file chosen in Settings (Classic): applied once a document's commands are here; one
  // giving Ctrl+I to inset-toggle (LyX's own key) takes it from italic, as Settings' Ctrl+I choice does
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  useEffect(() => {
    const apply = () => {
      const file = pendingLyxBind();
      const all = entriesRef.current;
      if (!file || !all.some(e => e.id === 'Edit ▸ Text Style ▸ Bold')) return;
      clearPendingLyxBind();
      const plan = planBind(file.text, all);
      for (const b of plan.set) setBinding(b.id, b.key);
      const ids = all.map(e => e.id);
      const inset = ctrlINow(getBindings(), ids) === 'inset';
      if (inset) for (const b of ctrlIPlan('inset', ids)) setBinding(b.id, b.key);
      const other = plan.includes.filter(f => !/^(cua|menus|math)$/.test(f));
      const lines = [
        plan.set.length ? `${plan.set.length} shortcut${plan.set.length === 1 ? '' : 's'} from ${file.name} now apply (on top of LyX's default keys, which OverLyX uses anyway).` : `Nothing in ${file.name} differs from LyX's default keys, which OverLyX uses already.`,
        ...(other.length ? [`It builds on LyX's ${other.join(', ')} keymap — choose that .bind file too to take its keys.`] : []),
        ...(plan.skipped.length ? ['', `Left out (${plan.skipped.length}):`, ...plan.skipped.slice(0, 10).map(k => `• ${k.keys} → ${k.lfun}: ${k.why}`), ...(plan.skipped.length > 10 ? [`… and ${plan.skipped.length - 10} more`] : [])] : []),
        ...(inset ? ['', `${formatShortcut('Ctrl+I')} opens and closes the inset at the cursor, as in LyX; italic is in Edit ▸ Text Style.`] : []),
        '', 'Help ▸ the search lists every shortcut; ✎ changes one, ↺ puts its default back.',
      ];
      void uiAlert('LyX Key Bindings', lines.join('\n'));
    };
    apply();
    window.addEventListener(LYXBIND_EVENT, apply);
    return () => window.removeEventListener(LYXBIND_EVENT, apply);
  }, [entries.length]);
  const live = useRef({ keyIndex, recording, open, searchIndex, overflowOpen });
  live.current = { keyIndex, recording, open, searchIndex, overflowOpen };

  const close = () => { setOpen(null); setOverflowOpen(false); setUserOpen(false); setRecording(null); const p = prevFocus.current; prevFocus.current = null; if (p && document.contains(p)) p.focus({ preventScroll: true }); };   // back to where the focus was — without scrolling to a caret that is off screen
  const openSearch = () => { if (live.current.searchIndex < 0) return; if (live.current.open === null && !live.current.overflowOpen) prevFocus.current = document.activeElement as HTMLElement | null; setOverflowOpen(false); setUserOpen(false); setOpen(live.current.searchIndex); };
  useEffect(() => {
    const bar = barRef.current!;
    const observer = new ResizeObserver(() => setCompact(bar.clientWidth < 1100));
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => { close(); }, [compact]);
  useEffect(() => {
    if (open === null && !userOpen && !overflowOpen) return;
    // a themed prompt/confirm/alert (Dialogs.tsx) is a real DOM overlay outside .menubar — clicking its
    // button must not look like "clicked away from the menu" and close the search/help panel under it
    const h = (e: MouseEvent) => { const t = e.target as HTMLElement; if (!t.closest('.menubar') && !t.closest('[role=dialog]')) close(); };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape' && !live.current.recording) close(); };
    document.addEventListener('mousedown', h); document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [open, userOpen, overflowOpen]);
  // global keys: the palette (Ctrl+Shift+P / F1), the user's shortcuts, and the swallowed default keys of rebound commands
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (live.current.recording || (e.target as HTMLElement).closest?.('[role=dialog]')) return;
      const k = keyFromEvent(e);
      if (!k) return;
      const paletteKey = canonical(effectiveShortcut(PALETTE_ID, paletteShortcut));
      if (k === paletteKey || (captureF1 && k === 'F1')) { e.preventDefault(); e.stopPropagation(); openSearch(); return; }
      const { custom, shadowed } = live.current.keyIndex;
      const hit = custom.get(k);
      if (hit?.action) { e.preventDefault(); e.stopPropagation(); if (live.current.open !== null || live.current.overflowOpen) close(); recordUsage('shortcut', menuKey(hit.path, hit.stat ?? hit.label)); hit.action(); return; }
      if (shadowed.has(k)) { e.preventDefault(); e.stopPropagation(); }
    };
    const onOpen = () => openSearch();
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('ol:palette', onOpen);
    return () => { window.removeEventListener('keydown', onKey, true); window.removeEventListener('ol:palette', onOpen); };
  }, [paletteShortcut, captureF1]);
  const toggle = (i: number) => {
    if (open === i) { close(); return; }
    if (open === null && !overflowOpen) prevFocus.current = document.activeElement as HTMLElement | null;
    setOverflowOpen(false);
    setUserOpen(false);
    setOpen(i);
  };
  const toggleOverflow = () => {
    if (overflowOpen || open !== null) { close(); return; }
    prevFocus.current = document.activeElement as HTMLElement | null;
    setUserOpen(false);
    setOverflowOpen(true);
  };
  const menuContent = (i: number) => menus[i].search
    ? <SearchMenu key={menus[i].title} menu={menus[i]} paletteShortcut={paletteShortcut} entries={entries} close={close} recording={recording} setRecording={setRecording} />
    : <MenuList key={menus[i].title} items={menus[i].items} path={[menus[i].title]} close={close}
      back={compact ? () => { setOpen(null); setOverflowOpen(true); } : modern ? () => barRef.current?.querySelector<HTMLElement>('.mm-title.on')?.focus() : undefined} escapeCloses={modern && !compact} />;
  const paletteKey = formatShortcut(effectiveShortcut(PALETTE_ID, paletteShortcut) ?? 'F1');
  return (
    <div class="menubar" ref={barRef}>
      {onHome && <a class="brand" href="#/" title="Start screen" onClick={e => { e.preventDefault(); onHome(); }}><Wordmark arrive /></a>}
      {title && <div class="menubar-title">{title}</div>}
      <div class="menubar-menus">
      {modern && !compact ? (
        <div class={'menu menu-main' + (open !== null ? ' open' : '')}>
          <button type="button" class="menu-main-btn" aria-haspopup="menu" aria-expanded={open !== null} aria-label="Menu" title={`Menu — or search the menus (${paletteKey})`} data-main-menu
            onMouseDown={e => { e.preventDefault(); if (open !== null) close(); else toggle(Math.max(0, menus.findIndex(m => !m.search))); }}
            onKeyDown={e => { if (['ArrowDown', 'Enter', ' '].includes(e.key)) { e.preventDefault(); if (open === null) toggle(0); } }}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          </button>
          {open !== null && <div class="menu-main-panel" role="presentation">
            <div class="mm-titles" role="menubar" aria-orientation="vertical">
              {onHome && <><button type="button" class="mm-title mm-home" onMouseDown={e => { e.preventDefault(); close(); onHome(); }}>Start screen</button><div class="menu-sep" /></>}
              {menus.map((m, i) => (
                <button key={m.title} type="button" class={'mm-title' + (open === i ? ' on' : '')} aria-haspopup="menu" aria-expanded={open === i}
                  onMouseEnter={() => setOpen(i)} onMouseDown={e => { e.preventDefault(); setOpen(i); }}
                  onKeyDown={e => {
                    const all = [...(e.currentTarget.parentElement?.querySelectorAll<HTMLElement>('.mm-title:not(.mm-home)') ?? [])];
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); const n = (i + (e.key === 'ArrowDown' ? 1 : menus.length - 1)) % menus.length; setOpen(n); all[n]?.focus(); }
                    else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); (e.currentTarget.closest('.menu-main-panel')?.querySelector<HTMLElement>('.mm-items [role="menuitem"]:not([aria-disabled="true"]), .mm-items input'))?.focus(); }
                  }}>{m.title}</button>
              ))}
            </div>
            <div class="mm-items">{menuContent(open)}</div>
          </div>}
        </div>
      ) : compact ? (
        <div class={'menu menu-overflow' + (overflowOpen || open !== null ? ' open' : '')}>
          <button type="button" aria-haspopup="menu" aria-expanded={overflowOpen || open !== null} onMouseDown={e => e.preventDefault()} onClick={toggleOverflow}
            onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); if (!overflowOpen && open === null) toggleOverflow(); } }}>{modern ? <svg class="menu-main-icon" viewBox="0 0 24 24" aria-label="Menu"><path d="M4 7h16M4 12h16M4 17h16" /></svg> : <>Menu <span aria-hidden="true">▾</span></>}</button>
          {(overflowOpen || open !== null) && <div class="menu-overflow-panel">
            {open === null
              ? <MenuList items={[...(onHome ? [{ label: 'Start screen', action: onHome }, { sep: true }] : []), ...menus.map((m, i) => ({ label: m.title, action: () => toggle(i) }))]} path={['Menu']} close={close} />
              : <><button type="button" class="menu-back" onClick={() => { setOpen(null); setOverflowOpen(true); }}>‹ All menus <span>{menus[open].title}</span></button>{menuContent(open)}</>}
          </div>}
        </div>
      ) : menus.map((m, i) => (
        <div key={m.title} class={'menu' + (open === i ? ' open' : '')} onMouseEnter={() => { if (open !== null && open !== i) toggle(i); }}>
          <button aria-haspopup="menu" aria-expanded={open === i} onKeyDown={e => {
            if (['ArrowDown', 'Enter', ' '].includes(e.key)) { e.preventDefault(); toggle(i); }
            else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); const next = (i + (e.key === 'ArrowRight' ? 1 : menus.length - 1)) % menus.length; (e.currentTarget.closest('.menubar-menus')?.querySelectorAll<HTMLElement>(':scope > .menu > button')[next])?.focus(); }
          }} onMouseDown={e => { e.preventDefault(); toggle(i); }} title={m.search ? `Search menus and shortcuts (${paletteKey})` : undefined}>{m.title}</button>
          {open === i && menuContent(i)}
        </div>
      ))}
      </div>
      <div class="menubar-primary">{primary}</div>
      <div class="menubar-actions">
      {right}
      {users && users.length > 0 && <UserAvatars users={users} onJump={onJumpToUser} />}
      {onShare && (
        <button type="button" class="share-btn" data-share aria-label="Share" onClick={onShare} title={shareTitle ?? 'Share this project: invite people or turn on a link'}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="10.5" width="14" height="10" rx="1.6" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></svg>
          <span class="share-label">Share</span>
        </button>
      )}
      {user?.guest && onSignIn && (
        <button type="button" class="share-btn signin-btn" data-signin onClick={onSignIn} title="You are here as a guest — sign in to keep this project in your account">
          Sign in
        </button>
      )}
      <a class="gh-link" href="https://github.com/japhba/overlyx" target="_blank" rel="noopener" title="OverLyX on GitHub — source code, issues, releases (GPL-3.0)">
        <svg viewBox="0 0 16 16" aria-hidden="true"><path fill="currentColor" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.42 7.42 0 0 1 2-.27c.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z"/></svg>
      </a>
      {showThemeToggle && <ThemeToggle onSettings={onAppearance} />}
      {user && <div class={'menu user-menu' + (userOpen ? ' open' : '')}>
        <button type="button" class="avatar-btn" data-user-menu title={`${user.name} (${user.username})`} onMouseDown={e => { e.preventDefault(); if (open !== null) setOpen(null); setUserOpen(o => !o); }}>
          <span class="avatar" style={{ background: user.color, color: inkOn(user.color) }} data-initials={user.avatar ? undefined : initials(user.name).length}><AvatarContent name={user.name} src={user.avatar} /></span>
        </button>
        {userOpen && <MenuList items={[{ label: user.name, disabled: true }, { label: user.guest ? 'guest (not signed in)' : user.username, disabled: true }, { sep: true },
          ...(user.guest && onSignIn ? [{ label: 'Sign in to keep this project…', action: onSignIn }] : onSettings ? [{ label: 'Settings…', action: onSettings }] : []),
          { label: user.guest ? 'Leave' : 'Sign out', action: onLogout }]} path={['Account']} close={close} style="left:auto;right:0" />}
      </div>}
      </div>
    </div>
  );
}
